import React, { useState, useEffect } from 'react';

const SCAN_FREQUENCY_OPTIONS = [
  { value: 'disabled', label: 'Disabled' },
  { value: '1h', label: 'Every hour' },
  { value: '3h', label: 'Every 3 hours' },
  { value: '6h', label: 'Every 6 hours' },
  { value: '12h', label: 'Every 12 hours' },
  { value: '24h', label: 'Daily' },
  { value: '48h', label: 'Every 2 days' },
  { value: '7d', label: 'Weekly' }
];

const CampaignAutomationSettings = ({ campaignId, isOpen, onClose, onSave }) => {
  const [settings, setSettings] = useState({
    autoScanEnabled: false,
    scanFrequency: '24h',
    autoAddContent: false,
    minViewThreshold: 0,
    liveMonitoringEnabled: false,
    notifyOnLive: true,
    notifyOnNewContent: true,
    notifyEmail: '',
    notifySlackWebhook: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/automation`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && campaignId) {
      fetchSettings();
    }
  }, [isOpen, campaignId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/automation`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      if (onSave) onSave(settings);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Automation Settings</h2>
            <p className="text-sm text-gray-500">Configure automatic content scanning and notifications</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin text-4xl">⏳</div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Auto Scan Section */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  🔄 Scheduled Scanning
                </h3>
                
                <div className="space-y-4 pl-2">
                  {/* Enable Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium text-gray-700">Enable Auto-Scan</label>
                      <p className="text-sm text-gray-500">Automatically scan for new content on a schedule</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.autoScanEnabled}
                        onChange={(e) => handleChange('autoScanEnabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sc-pink/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sc-pink"></div>
                    </label>
                  </div>

                  {/* Frequency */}
                  {settings.autoScanEnabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Scan Frequency
                        </label>
                        <select
                          value={settings.scanFrequency}
                          onChange={(e) => handleChange('scanFrequency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink"
                        >
                          {SCAN_FREQUENCY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Auto Add */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="font-medium text-gray-700">Auto-Add Content</label>
                          <p className="text-sm text-gray-500">Automatically add discovered content without review</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.autoAddContent}
                            onChange={(e) => handleChange('autoAddContent', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sc-pink/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sc-pink"></div>
                        </label>
                      </div>

                      {/* Min Views Threshold */}
                      {settings.autoAddContent && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Minimum View Threshold
                          </label>
                          <input
                            type="number"
                            value={settings.minViewThreshold}
                            onChange={(e) => handleChange('minViewThreshold', parseInt(e.target.value) || 0)}
                            placeholder="0 = no minimum"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink"
                          />
                          <p className="text-xs text-gray-500 mt-1">Only auto-add content with at least this many views (0 = add all)</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </section>

              {/* Live Monitoring Section */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  🔴 Live Stream Monitoring
                </h3>
                
                <div className="space-y-4 pl-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium text-gray-700">Enable Live Monitoring</label>
                      <p className="text-sm text-gray-500">Get alerted when campaign creators go live with the game</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.liveMonitoringEnabled}
                        onChange={(e) => handleChange('liveMonitoringEnabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sc-pink/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>

                  {settings.liveMonitoringEnabled && (
                    <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        <strong>How it works:</strong> We'll check every 5 minutes if any creators in this campaign 
                        are streaming the campaign's game on Twitch. You'll receive notifications based on your settings below.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Notifications Section */}
              <section>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  🔔 Notifications
                </h3>
                
                <div className="space-y-4 pl-2">
                  {/* Notify on Live */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium text-gray-700">Notify on Live Streams</label>
                      <p className="text-sm text-gray-500">Get notified when a creator goes live</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.notifyOnLive}
                        onChange={(e) => handleChange('notifyOnLive', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sc-pink/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sc-pink"></div>
                    </label>
                  </div>

                  {/* Notify on New Content */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium text-gray-700">Notify on New Content</label>
                      <p className="text-sm text-gray-500">Get notified when new content is discovered</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.notifyOnNewContent}
                        onChange={(e) => handleChange('notifyOnNewContent', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sc-pink/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sc-pink"></div>
                    </label>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Notifications
                    </label>
                    <input
                      type="email"
                      value={settings.notifyEmail}
                      onChange={(e) => handleChange('notifyEmail', e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank to disable email notifications</p>
                  </div>

                  {/* Slack Webhook */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slack Webhook URL
                    </label>
                    <input
                      type="url"
                      value={settings.notifySlackWebhook}
                      onChange={(e) => handleChange('notifySlackWebhook', e.target.value)}
                      placeholder="https://hooks.slack.com/services/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sc-pink"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank to disable Slack notifications</p>
                  </div>
                </div>
              </section>

              {/* Error */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-6 py-2 bg-sc-pink text-white rounded-lg hover:bg-sc-pink/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignAutomationSettings;
